import { RadioButtonCard, RadioGroup } from '@orn/shared-web/components/RadioButton/RadioButtons';
import * as React from 'react';

import { trackFeatureInterest } from '../../../lib/mixpanel';
import { useAuthState } from '../../../providers/AuthProvider';
import { OnboardingTitle } from '../../OnboardingLayout';
import { truncateForCount } from '../dependants/helpers';
import { createEmptyPendingDependant, useOnboardingContext, type DependantCount } from '../OnboardingContext';

import OnboardingStepActions from './OnboardingStepActions';

import type { IOnboardingStepProps } from '../types';

/**
 * Non-selectable third option. Multiple people with separate finances isn't
 * supported yet, so this renders as a disabled card outside the `RadioGroup`
 * (a radio can't hold an interactive link, and we never want it selectable).
 * The trailing slot shows an italic "Coming soon" where the radio dot sits on
 * the real options. The link logs the signed-in user's interest so they can be
 * emailed when the feature ships.
 */
const UnsupportedScenarioCard = () => {
  const { user } = useAuthState();
  const [notified, setNotified] = React.useState(false);

  const handleNotify = () => {
    // Onboarding is auth-gated, so `user.email` is always present here — the
    // guard only satisfies the nullable type, and the confirmation below
    // always reflects a fired event.
    if (user?.email) {
      trackFeatureInterest({ feature: 'Separate finances', email: user.email });
    }
    setNotified(true);
  };

  return (
    <div className="bg-disabled_subtle shadow-xs ring-secondary flex w-full items-start gap-2 rounded-xl p-4 ring-1 ring-inset">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-md text-secondary">Multiple people with separate finances</p>
        {notified ? (
          <span className="text-brand-secondary text-sm font-semibold">
            Thanks &mdash; we&rsquo;ll email you when it&rsquo;s ready.
          </span>
        ) : (
          <span className="text-tertiary text-sm">
            <button
              type="button"
              onClick={handleNotify}
              className="text-brand-secondary hover:text-brand-secondary_hover font-semibold underline decoration-transparent underline-offset-2 hover:decoration-current"
            >
              Notify me
            </button>{' '}
            when Mantle supports multiple people with separate finances (e.g. a parent and an in-law).
          </span>
        )}
      </div>
      <span className="text-tertiary shrink-0 pt-0.5 text-sm italic">Coming soon</span>
    </div>
  );
};
UnsupportedScenarioCard.displayName = 'UnsupportedScenarioCard';

/**
 * Loved-ones count picker. The first screen of the dependant flow — captures
 * "one person" vs. "two people with shared finances" by sizing
 * `pendingDependants` to the chosen count (the array length *is*
 * `dependantCount`). The chosen value persists across back-navigation within
 * the onboarding flow's React tree.
 *
 * On resume (or back-nav) the radio pre-selects to the existing count: the
 * flow seeds `pendingDependants` from the user's saved dependants before this
 * screen mounts, so `dependantCount` is already populated and the user can
 * continue forward without re-picking.
 *
 * On continue, resizes `pendingDependants` to the chosen count — growing pads
 * empty slots, shrinking captures any dropped server row into
 * `pendingDependantDeletions` so the review step deletes it rather than
 * orphaning it.
 *
 * Figma: Mantle - UX/UI, node 4154:68017 ("Onboarding Step 07 - Loved Ones Amount").
 */
const DependantCountStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const { dependantCount, pendingDependants, setPendingDependants, setPendingDependantDeletions } =
    useOnboardingContext();
  const [selected, setSelected] = React.useState<DependantCount | null>(dependantCount);

  const handleContinue = () => {
    if (selected === null) {
      return;
    }
    if (pendingDependants.length !== selected) {
      const next = truncateForCount(pendingDependants, selected, createEmptyPendingDependant);
      // Capture any dropped slot's serverId so the review step can delete it.
      // Slots without a serverId are pure local state and just disappear.
      const droppedServerIds = pendingDependants
        .slice(selected)
        .map((slot) => slot.serverId)
        .filter((id): id is string => id !== null);
      if (droppedServerIds.length > 0) {
        // Dedupe — a count yo-yo (e.g. 2→1→2→1) would otherwise queue the
        // same id twice, firing redundant (but harmless) delete calls.
        setPendingDependantDeletions((prev) => Array.from(new Set([...prev, ...droppedServerIds])));
      }
      setPendingDependants(next);
    }
    onNext();
  };

  return (
    <>
      <OnboardingTitle title="Who are you helping?" onBack={onBack} />

      <div className="flex w-full flex-col gap-6">
        <div className="flex flex-col gap-2">
          <RadioGroup
            aria-label="Number of loved ones"
            value={selected ? String(selected) : null}
            onChange={(next) => setSelected(next === '2' ? 2 : 1)}
            className="gap-2"
          >
            <RadioButtonCard value="1" label="One person" />
            <RadioButtonCard
              value="2"
              label="Two people with shared finances"
              hint="e.g. A couple with shared bank accounts, joint expenses, or co-mingled assets"
            />
          </RadioGroup>

          <UnsupportedScenarioCard />
        </div>

        <OnboardingStepActions onContinue={handleContinue} isContinueDisabled={selected === null} />
      </div>
    </>
  );
};

DependantCountStep.displayName = 'DependantCountStep';

export default DependantCountStep;
